/** Small builders shared by every render pass. */

import { type BufferGeometry, Mesh, type MeshStandardMaterial, Vector3 } from "three";
import { G } from "./geometries.ts";

export function V(x: number, y: number, z: number): Vector3 {
  return new Vector3(x, y, z);
}

/** Yaw that points local +Z along (dx, dz). */
export function yawOf(dx: number, dz: number): number {
  return Math.atan2(dx, dz);
}

/** A unit-height cylinder stretched and rotated to span a → b. */
export function tubeBetween(
  a: Vector3,
  b: Vector3,
  geometry: BufferGeometry,
  material: MeshStandardMaterial,
): Mesh {
  const delta = new Vector3().subVectors(b, a);
  const length = delta.length();
  const mesh = new Mesh(geometry, material);

  mesh.scale.y = length;
  mesh.position.copy(a).addScaledVector(delta, 0.5);
  mesh.quaternion.setFromUnitVectors(V(0, 1, 0), delta.normalize());
  mesh.castShadow = true;

  return mesh;
}

export function boxAt(
  cx: number,
  cy: number,
  cz: number,
  sx: number,
  sy: number,
  sz: number,
  material: MeshStandardMaterial,
  rotY = 0,
): Mesh {
  const mesh = new Mesh(G.box, material);

  mesh.position.set(cx, cy, cz);
  mesh.scale.set(sx, sy, sz);
  mesh.rotation.y = rotY;
  mesh.castShadow = true;
  mesh.receiveShadow = true;

  return mesh;
}

/** A box rotated about Z — the sloped stringers and flights. */
export function slopedBox(
  cx: number,
  cy: number,
  cz: number,
  len: number,
  h: number,
  t: number,
  angle: number,
  material: MeshStandardMaterial,
): Mesh {
  const mesh = new Mesh(G.box, material);

  mesh.position.set(cx, cy, cz);
  mesh.scale.set(len, h, t);
  mesh.rotation.z = angle;
  mesh.castShadow = true;
  mesh.receiveShadow = true;

  return mesh;
}
